import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import styled from "styled-components/macro";
import {
  ArchiveButton,
  ButtonContainer,
  ButtonWithConfirmDialog,
  EditButton,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import {
  CaretRight,
  SystemDefaultIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import type {
  ListPatchArgSchema,
  PaginatedListSchema,
  ShortListSchema,
} from "../../../../../types/types.PIM";
import type { AxiosError } from "axios";
import Axios from "axios";
import { endpoints } from "../../../../../endpoints";
import {
  checkTextOverflow,
  formatDateTime,
  highlightTextByQuery,
  useFormWrapper,
  useStoreState,
} from "../../../../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import ReactTooltip from "react-tooltip";
import type { ListItemFormValue } from "./ListHome";
import { IconContainer, MatchCount } from "./ListHome";
import { SmallSectionHeaderRegular } from "../../../../../components/Typography/Typography";
import type { DataMutate } from "../../../../../types/types";
import { SystemIconWrapper } from "../components/PIM.components.util";
import { useAuthContext } from "../../../../../components/Auth";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { Form } from "../../../../../layout/FormLayout";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { InfoBlockSmall } from "../../../../../components/InfoBlocks/InfoBlocks";
import { z } from "zod";
import { strings } from "../../../../../util/strings";

export const ListItemBase = styled.div`
  height: 58px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${({ theme }) => theme.resolve("spacing_xl")};
  cursor: pointer;
  > h3 {
    flex: 1 0 0.5;
  }
  border-top: ${({ theme }) =>
    `${theme.resolve("border_xs")} solid ${theme.secondaryBorder}`};
  border-bottom: ${({ theme }) =>
    `${theme.resolve("border_xs")} solid ${theme.primaryBG}`};
  border-right: ${({ theme }) =>
    `${theme.resolve("border_2xl")} solid ${theme.secondaryBorder}`};
  border-left: ${({ theme }) => `3px solid ${theme.secondaryBorder}`};
  background-color: ${({ theme }) => theme.colors.white};
`;

export const ListItemWrapper = styled(ListItemBase)<{ active?: boolean }>`
  > div {
    > button,
    button:disabled {
      opacity: 0;
    }
  }
  &:hover {
    > div {
      > button {
        opacity: 1;
      }
      > button:disabled {
        opacity: 0.5;
      }
    }
  }
  border-top: ${({ theme, active }) =>
    active
      ? `${theme.resolve("border_md")} solid ${theme.brandColor}`
      : `${theme.resolve("border_xs")} solid ${theme.secondaryBorder}`};
  border-bottom: ${({ theme, active }) =>
    active
      ? `${theme.resolve("border_md")} solid ${theme.brandColor}`
      : `${theme.resolve("border_xs")} solid transparent`};
  border-right: ${({ theme, active }) =>
    active
      ? `${theme.resolve("border_2xl")} solid ${theme.brandColor}`
      : `${theme.resolve("border_2xl")} solid ${theme.secondaryBorder}`};
  border-left: ${({ theme, active }) =>
    active
      ? `3px solid ${theme.brandColor}`
      : `3px solid ${theme.secondaryBorder}`};
  background-color: ${({ theme, active }) =>
    active ? theme.primaryButtonBG : theme.colors.white};
  justify-content: flex-start;
`;

export const ListItemName = styled(SmallSectionHeaderRegular)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
`;

export const ListSchemaFn = (
  t: TFunction,
  maxLength?: number,
  is_system?: boolean
) =>
  z.object({
    name: is_system
      ? z.string().optional()
      : z
          .string()
          .min(1, strings(t).thisIsARequiredField)
          .max(
            maxLength ?? 25,
            t("Maximum {{chars}} characters allowed", {
              chars: maxLength ?? 25,
            })
          ),
    icons_enabled: z.boolean(),
  });

export const ListItem = ({
  item,
  active,
  selectItem,
  mutateList,
  searchQuery,
}: {
  item: ShortListSchema;
  active: boolean;
  selectItem: (reset?: boolean) => void;
  mutateList: DataMutate<PaginatedListSchema>;
  searchQuery: string;
}) => {
  const [editMode, setEditMode] = useState(false);
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);

  const editNameRef =
    useRef<{ setEditMode: (editMode: boolean) => void; focus: () => void }>(
      null
    );
  const nameRef = useRef<HTMLHeadingElement>(null);
  const { hasPermission } = useAuthContext();

  const { handleSubmit, register, formState, errors, watch } = useFormWrapper({
    resolver: zodResolver(ListSchemaFn(t, 25, item.is_system)),
    defaultValues: {
      name: item.name,
      icons_enabled: item.icons_enabled,
    },
    shouldUnregister: false,
  });

  const is_icon_enabled = watch("icons_enabled");
  const handleDeleteListItem = async () => {
    try {
      await Axios.patch<ListPatchArgSchema, ShortListSchema>(
        endpoints.v2_tenants_id_or_slug_pim_lists_id(tenant_id, item.id),
        {
          is_deleted: true,
        }
      );
      notifySuccess(t("List archived successfully"));
      mutateList();
      selectItem(true);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not archive list. Something went wrong."),
        {
          error,
        }
      );
    }
  };

  const handleEditClick = () => {
    setEditMode(true);
    setTimeout(() => editNameRef.current?.setEditMode(true));
  };

  const handleEditListConfirm = async ({
    name,
    icons_enabled,
  }: ListItemFormValue) => {
    try {
      await Axios.patch<ListPatchArgSchema, ShortListSchema>(
        endpoints.v2_tenants_id_or_slug_pim_lists_id(tenant_id, item.id),
        {
          ...(item.is_system ? {} : { name: name.trim() }),
          icons_enabled: icons_enabled,
        }
      );
      notifySuccess(t("List updated successfully"));
      setEditMode(false);
      mutateList();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not edit list. Something went wrong."),
        {
          error,
        }
      );
    }
  };

  const isTextOverflow = () =>
    nameRef.current ? checkTextOverflow(nameRef.current).widthOverflow : false;

  useEffect(() => {
    if (!active && editMode) {
      setEditMode(false);
    }
  }, [active, editMode]);

  return (
    <ListItemWrapper active={active} onClick={(e) => selectItem()}>
      <ListItemName
        ref={nameRef}
        data-for={`list-item-name-${item.id}-tooltip`}
        data-tip={isTextOverflow() ? `${item.name} (${item.count ?? 0})` : ""}
        dangerouslySetInnerHTML={{
          __html: `${highlightTextByQuery(item.name, searchQuery)} (${
            item.count ?? 0
          })`,
        }}
      />
      <ReactTooltip id={`list-item-name-${item.id}-tooltip`} />
      <SystemIconWrapper
        style={{
          padding: 0,
          position: "relative",
          top: "2px",
        }}
        data-for={`system${item.id}`}
        data-tip={t("System Default")}
      >
        {item.is_system && <SystemDefaultIcon width={18} height={18} />}
      </SystemIconWrapper>
      {item.is_system && <ReactTooltip id={`system${item.id}`} />}
      <IconContainer>
        <>
          {hasPermission("delete_lists") && (
            <ButtonWithConfirmDialog
              Button={(props) => (
                <>
                  <ArchiveButton
                    {...props}
                    width={24}
                    height={24}
                    datafor={`list-item-${item.id}-delete-tooltip`}
                  />
                  <ReactTooltip id={`list-item-${item.id}-delete-tooltip`} />
                </>
              )}
              testid={`list-item-${item.id}`}
              handleConfirm={handleDeleteListItem}
              confirmMessage={t(
                "Are you sure you want to archive this list, {{itemName}}?",
                { itemName: item.name }
              )}
            />
          )}
          {hasPermission("modify_lists") && (
            <EditButton
              onClick={handleEditClick}
              width={24}
              height={24}
              datafor={`list-item-${item.id}-tooltip`}
            />
          )}
        </>
        {searchQuery && Boolean(searchQuery.trim().length) && (
          <MatchCount>
            {item && item.number_of_matches
              ? item.number_of_matches === 1
                ? t("1 match")
                : item.number_of_matches > 1
                ? t("{{count}} matches", { count: item.number_of_matches })
                : t("No match")
              : t("No match")}
          </MatchCount>
        )}
        <CaretRight />
        <ReactTooltip id={`list-item-${item.id}-tooltip`} />
      </IconContainer>

      {item && (
        <SlideOut
          title={t("Edit List")}
          show={!!editMode}
          closeFlyout={() => setEditMode(false)}
        >
          <Form noValidate onSubmit={handleSubmit(handleEditListConfirm)}>
            <TextField
              name="name"
              label={t("List Name")}
              theref={register({
                required: true,
              })}
              disabled={item.is_system}
              formState={formState}
              errors={errors}
              type="text"
            />
            <div style={{ marginBottom: "25px" }}>
              <ToggleSwitchV2
                label={t("Enable icons for list values")}
                name="icons_enabled"
                defaultChecked={item.icons_enabled}
                ref={register({ required: true })}
                checked={is_icon_enabled}
                style={{ marginBottom: "30px" }}
              />
            </div>
            <InfoBlockSmall
              header={t("List Values")}
              content={item.count.toString()}
            />
            <InfoBlockSmall
              header={t("Created On")}
              content={formatDateTime(item.created_at) || "-"}
            />
            <InfoBlockSmall
              header={t("Created By")}
              content={item.created_by || "-"}
            />
            <InfoBlockSmall
              header={t("Last Modified On")}
              content={formatDateTime(item.modified_at) || "-"}
            />
            <InfoBlockSmall
              header={t("Last Modified By")}
              content={item.modified_by || "-"}
            />

            <ButtonContainer style={{ marginTop: "30px" }}>
              <TertiaryButtonLargeV2
                onClick={() => setEditMode(false)}
                fullWidth
              >
                {t("Cancel")}
              </TertiaryButtonLargeV2>
              <PrimaryButtonLargeV2 type="submit" fullWidth>
                {t("Save")}
              </PrimaryButtonLargeV2>
            </ButtonContainer>
          </Form>
        </SlideOut>
      )}
    </ListItemWrapper>
  );
};
